import { useCallback, useState } from 'react';
import { queryParamToString } from '@moda/portal-stanchions';
import { AuthError, ERROR_MESSAGES } from '../../lib/authenticate';
import {
  completeEmailAuthRegistration,
  confirmEmailAuthMagicLink,
  EmailAuthResult,
  PasswordlessStartResult,
  requestPasswordlessLogin,
  resendPasswordlessEmail,
  verifyEmailAuthOtp
} from '../../lib/passwordless';
import { CONFIG } from '../../config';
import { tc } from '../../lib/trackingContext';
import { useCookies } from '../useCookies';
import { useSessionQueryParams } from '../useSessionQueryParams';
import { useCompleteAuth } from '../useAuth/useCompleteAuth';

type PasswordlessRequestArgs = {
  email: string;
};

type PasswordlessVerifyArgs = {
  challengeId: string;
  otp: string;
  remember?: boolean;
};

type PasswordlessConfirmArgs = {
  challengeId: string;
  uid: string;
  remember?: boolean;
};

type CompleteRegistrationArgs = {
  authorizationToken?: string | null;
  email: string;
  registrationToken: string;
  firstName: string;
  lastName: string;
  remember?: boolean;
  optedIn?: boolean;
  countryCode?: string;
  genderPreference?: string;
};

const normalizePasswordlessError = (error: unknown): AuthError => {
  if (error instanceof Error) {
    return {
      code: 'default',
      message: error.message || ERROR_MESSAGES.default
    };
  }

  if (error && typeof error === 'object' && 'code' in error) {
    return error as AuthError;
  }

  return {
    code: 'default',
    message: ERROR_MESSAGES.default
  };
};

export const usePasswordlessAuth = () => {
  const [error, setError] = useState<AuthError>();
  const [loading, setLoading] = useState(false);
  const completeAuth = useCompleteAuth();
  const {
    cookies: { cartId }
  } = useCookies();
  const {
    params: { bypass_rack_attack: bypassRackAttackQueryParameter }
  } = useSessionQueryParams();

  const bypassRackAttack =
    queryParamToString(bypassRackAttackQueryParameter) || CONFIG.BYPASS_RACK_ATTACK;

  const runRequest = useCallback(
    async <TResult>(
      callback: () => Promise<TResult>,
      { onError }: { onError?: (error: AuthError) => void } = {}
    ) => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await callback();
        setLoading(false);
        return result;
      } catch (error) {
        const normalizedError = normalizePasswordlessError(error);
        setError(normalizedError);
        onError?.(normalizedError);
        setLoading(false);
        throw normalizedError;
      }
    },
    []
  );

  const finalizeEmailAuthResult = useCallback(
    async (
      result: EmailAuthResult,
      remember?: boolean,
      optedIn?: boolean
    ): Promise<EmailAuthResult> => {
      if (result.type === 'registration_required') {
        return result;
      }

      const user = await completeAuth({ user: result.user, remember, opted_in: optedIn });

      return {
        type: 'authenticated',
        user
      };
    },
    [completeAuth]
  );

  const clearError = useCallback(() => setError(undefined), []);

  const requestLogin = useCallback(
    ({ email }: PasswordlessRequestArgs) => {
      tc.track('Login Started');

      return runRequest<PasswordlessStartResult>(() =>
        requestPasswordlessLogin({ email, bypassRackAttack })
      );
    },
    [bypassRackAttack, runRequest]
  );

  const resendEmail = useCallback(
    ({ challengeId }: { challengeId: string }) =>
      runRequest(() => resendPasswordlessEmail({ challengeId, bypassRackAttack })).then(
        response => response.message
      ),
    [bypassRackAttack, runRequest]
  );

  const verifyOtp = useCallback(
    ({ challengeId, otp, remember }: PasswordlessVerifyArgs) =>
      runRequest(
        async () => {
          const result = await verifyEmailAuthOtp({
            challengeId,
            otp,
            cartId,
            bypassRackAttack
          });

          tc.track('OTP Completed');

          return finalizeEmailAuthResult(result, remember);
        },
        {
          onError: error => {
            tc.track('OTP Failed', {
              addToTrackingEvent: {
                code: error.code,
                message: error.message || ERROR_MESSAGES.default
              }
            });
          }
        }
      ),
    [bypassRackAttack, cartId, finalizeEmailAuthResult, runRequest]
  );

  const confirmMagicLink = useCallback(
    ({ challengeId, uid, remember }: PasswordlessConfirmArgs) =>
      runRequest(
        async () => {
          const result = await confirmEmailAuthMagicLink({
            challengeId,
            cartId,
            uid,
            bypassRackAttack
          });

          tc.track('Magic Link Completed');

          return finalizeEmailAuthResult(result, remember);
        },
        {
          onError: error => {
            tc.track('Magic Link Failed', {
              addToTrackingEvent: {
                code: error.code,
                message: error.message || ERROR_MESSAGES.default
              }
            });
          }
        }
      ),
    [bypassRackAttack, cartId, finalizeEmailAuthResult, runRequest]
  );

  const completeRegistration = useCallback(
    ({
      authorizationToken,
      email,
      registrationToken,
      firstName,
      lastName,
      remember,
      optedIn,
      countryCode,
      genderPreference
    }: CompleteRegistrationArgs) =>
      runRequest(async () => {
        const user = await completeEmailAuthRegistration({
          authorizationToken,
          email,
          registrationToken,
          firstName,
          lastName,
          optedIn,
          countryCode,
          cartId,
          genderPreference,
          bypassRackAttack
        });

        const result = await finalizeEmailAuthResult(
          {
            type: 'authenticated',
            user
          },
          remember,
          optedIn
        );

        if (result.type === 'authenticated') {
          tc.track('Registration Completed', {
            addToTrackingEvent: {
              label: result.user.id
            }
          });
        }

        return result;
      }),
    [bypassRackAttack, cartId, finalizeEmailAuthResult, runRequest]
  );

  return {
    clearError,
    completeRegistration,
    confirmMagicLink,
    error,
    loading,
    requestLogin,
    resendEmail,
    verifyOtp
  };
};
